import { useNavigation } from '@app/hooks/useNavigation';
import { useQuestionaireRoute } from '@app/hooks/useQuestionaireRoute';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { useCreateQuestionnaireMutation } from '@dieterApi/questionnaire/useCreateQuestionnaireMutation';
import { useOpenQuestionnaireMutation } from '@dieterApi/questionnaire/useOpenQuestionnaireMutation';
import { ApplicationClass } from '@dieterApi/user/useUserQuery';
import { useUserValueAddMutation } from '@dieterApi/user/useUserValueAddMutation';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { NavigationSection, NavigationSubsection, OnboardingApps, Topics } from './constants';
import { useUser } from './UserContext';

// ============================================================================
// Types
// ============================================================================

/**
 * Interactive states that require user action or represent stable waiting points
 */
export enum OnboardingState {
  OnboardingStart = 'OnboardingStart',
  OnboardingChatActive = 'OnboardingChatActive',
  FillingQuestionnaire = 'FillingQuestionnaire',
  AwaitingEmail = 'AwaitingEmail',
  AwaitingEmailConfirmation = 'AwaitingEmailConfirmation',
  DocumentPreviewWithCTA = 'DocumentPreviewWithCTA',
  AwaitingPassword = 'AwaitingPassword',
  Complete = 'Complete',
}

/**
 * Events that trigger state transitions
 */
export type OnboardingEvent =
  | { type: 'ANONYMOUS_USER_CREATED' }
  | { type: 'APP_SELECTED'; appId: string }
  | { type: 'APP_DESELECTED' }
  | { type: 'ACCOUNT_CREATE_REQUESTED'; appId?: string }
  | { type: 'QUESTIONNAIRE_COMPLETED'; questionnaireId: string }
  | { type: 'EMAIL_SUBMITTED'; email: string }
  | { type: 'EMAIL_CONFIRMED' }
  | { type: 'CONFIRMATION_RESET' }
  | { type: 'CTA_CLICKED' }
  | { type: 'PASSWORD_SUBMITTED' }
  | { type: 'RESET' }
  | { type: 'RESTORE'; state: OnboardingStateMachineState };

/**
 * Context data maintained throughout the onboarding flow
 */
export interface OnboardingContext {
  appId?: string;
  questionnaireId?: string;
  email?: string;
  confirmationToken?: string;
}

/**
 * State machine state
 */
export interface OnboardingStateMachineState {
  state: OnboardingState;
  context: OnboardingContext;
}

// ============================================================================
// State Machine Definition
// ============================================================================

/**
 * Initial state machine configuration
 */
const initialState: OnboardingStateMachineState = {
  state: OnboardingState.OnboardingStart,
  context: {},
};

/**
 * State transition map defining valid transitions and their target states
 */
const transitions: Record<OnboardingState, Partial<Record<OnboardingEvent['type'], OnboardingState>>> = {
  [OnboardingState.OnboardingStart]: {
    ANONYMOUS_USER_CREATED: OnboardingState.OnboardingChatActive,
  },
  [OnboardingState.OnboardingChatActive]: {
    APP_SELECTED: OnboardingState.FillingQuestionnaire,
    ACCOUNT_CREATE_REQUESTED: OnboardingState.AwaitingEmail,
  },
  [OnboardingState.FillingQuestionnaire]: {
    QUESTIONNAIRE_COMPLETED: OnboardingState.AwaitingEmail,
    APP_DESELECTED: OnboardingState.OnboardingChatActive,
  },
  [OnboardingState.AwaitingEmail]: {
    EMAIL_SUBMITTED: OnboardingState.AwaitingEmailConfirmation,
  },
  [OnboardingState.AwaitingEmailConfirmation]: {
    // EMAIL_CONFIRMED: Conditional - DocumentPreviewWithCTA (with appId) OR AwaitingPassword (without appId)
    EMAIL_CONFIRMED: OnboardingState.DocumentPreviewWithCTA, // Default, overridden in reducer for direct account flow
  },
  [OnboardingState.DocumentPreviewWithCTA]: {
    CTA_CLICKED: OnboardingState.AwaitingPassword,
    CONFIRMATION_RESET: OnboardingState.AwaitingEmailConfirmation,
  },
  [OnboardingState.AwaitingPassword]: {
    PASSWORD_SUBMITTED: OnboardingState.Complete,
  },
  [OnboardingState.Complete]: {},
};

/**
 * Reducer function implementing the state machine logic
 */
function onboardingStateMachineReducer(
  state: OnboardingStateMachineState,
  event: OnboardingEvent
): OnboardingStateMachineState {
  if (process.env.NODE_ENV === 'development') {
    console.log('[OnboardingStateMachine] Event received:', {
      currentState: state.state,
      event: event.type,
      eventData: event,
      context: state.context,
    });
  }

  // Handle reset event
  if (event.type === 'RESET') {
    if (process.env.NODE_ENV === 'development') {
      console.log('[OnboardingStateMachine] Resetting to initial state');
    }
    return initialState;
  }

  // Handle restore event (when loading persisted state)
  if (event.type === 'RESTORE') {
    if (process.env.NODE_ENV === 'development') {
      console.log('[OnboardingStateMachine] Restoring persisted state:', event.state);
    }
    return event.state;
  }

  // Get valid transitions for current state
  const validTransitions = transitions[state.state];
  let nextState = validTransitions?.[event.type];

  // Special case: EMAIL_CONFIRMED transition depends on context
  // If no appId (direct account creation) or website-scan flow, skip DocumentPreviewWithCTA
  if (event.type === 'EMAIL_CONFIRMED' && state.state === OnboardingState.AwaitingEmailConfirmation) {
    if (state.context.appId && state.context.appId !== OnboardingApps.WEBSITE_SCAN) {
      // Normal flow: User completed questionnaire, show document preview
      nextState = OnboardingState.DocumentPreviewWithCTA;
    } else {
      // Direct account creation or website-scan: No document to preview, go straight to password
      nextState = OnboardingState.AwaitingPassword;
      if (process.env.NODE_ENV === 'development') {
        console.log('[OnboardingStateMachine] Direct account creation detected, skipping document preview');
      }
    }
  }

  // If no valid transition exists, return current state unchanged
  if (!nextState) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `[OnboardingStateMachine] Invalid transition: Cannot handle event "${event.type}" in state "${state.state}"`
      );
    }
    return state;
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('[OnboardingStateMachine] Transitioning:', {
      from: state.state,
      to: nextState,
      event: event.type,
    });
  }

  // Build updated context based on event
  const updatedContext = { ...state.context };

  switch (event.type) {
    case 'APP_SELECTED':
      updatedContext.appId = event.appId;
      break;
    case 'APP_DESELECTED':
      // Clear the app selection
      updatedContext.appId = undefined;
      break;
    case 'QUESTIONNAIRE_COMPLETED':
      updatedContext.questionnaireId = event.questionnaireId;
      break;
    case 'EMAIL_SUBMITTED':
      updatedContext.email = event.email;
      break;
    case 'ACCOUNT_CREATE_REQUESTED':
      // Account creation request can optionally set appId (e.g. website-scan flow)
      if (event.appId) {
        updatedContext.appId = event.appId;
      }
      break;
    case 'EMAIL_CONFIRMED':
      // Email confirmation doesn't add new context data
      break;
    case 'CONFIRMATION_RESET':
      // Confirmation reset doesn't change context data
      break;
    case 'CTA_CLICKED':
      // CTA click doesn't add new context data
      break;
    case 'PASSWORD_SUBMITTED':
      // Password is handled via backend mutation, not stored in context
      break;
    case 'ANONYMOUS_USER_CREATED':
      // No context update needed
      break;
    default:
      const _exhaustiveCheck: never = event;
      return _exhaustiveCheck;
  }

  const newState = {
    state: nextState,
    context: updatedContext,
  };

  if (process.env.NODE_ENV === 'development') {
    console.log('[OnboardingStateMachine] New state:', {
      state: newState.state,
      context: newState.context,
    });
  }

  return newState;
}

// ============================================================================
// Context Provider
// ============================================================================

interface OnboardingStateMachineContextValue {
  currentState: OnboardingState;
  context: OnboardingContext;
  dispatch: React.Dispatch<OnboardingEvent>;
  canTransition: (eventType: OnboardingEvent['type']) => boolean;
}

const OnboardingStateMachineContext = createContext<OnboardingStateMachineContextValue | undefined>(undefined);

interface OnboardingStateMachineProviderProps {
  children: ReactNode;
  initialStateMachine?: OnboardingStateMachineState;
}

export function OnboardingStateMachineProvider({
  children,
  initialStateMachine = initialState,
}: OnboardingStateMachineProviderProps) {
  const { setNavigation, navigation, navigateDashboard, hasLoaded } = useNavigation();
  const history = useHistory();
  const { user, userLoading } = useUser();
  const { topics, questionnaires } = useUserWithCompany();
  const { navigateToPath } = useQuestionaireRoute();
  const [createQuestionnaire] = useCreateQuestionnaireMutation();
  const [openQuestionnaire] = useOpenQuestionnaireMutation();
  const [addUserValue] = useUserValueAddMutation();
  const isCreatingQuestionnaireRef = useRef(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasRestoredState, setHasRestoredState] = useState(false);

  // Track the last dispatched event to prevent duplicates
  const lastDispatchedEventRef = useRef<string | null>(null);

  // Always initialize with fresh state first
  // We'll restore from navigation.stateMachine in an effect after it loads
  const [stateMachine, rawDispatch] = useReducer(onboardingStateMachineReducer, initialStateMachine);

  // Wrapper around dispatch that prevents duplicate events
  const dispatch = useCallback(
    (event: OnboardingEvent) => {
      // Create a signature for this event
      const eventSignature = JSON.stringify({
        type: event.type,
        // Include event-specific data in signature
        ...(event.type === 'APP_SELECTED' && { appId: event.appId }),
        ...(event.type === 'QUESTIONNAIRE_COMPLETED' && { questionnaireId: event.questionnaireId }),
        ...(event.type === 'EMAIL_SUBMITTED' && { email: event.email }),
        // Don't include password in signature for security
        state: stateMachine.state, // Include current state to allow same event in different states
      });

      // Check if this exact event was just dispatched
      if (lastDispatchedEventRef.current === eventSignature) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[OnboardingStateMachine] Skipping duplicate event:', event.type);
        }
        return;
      }

      // Update last dispatched event
      lastDispatchedEventRef.current = eventSignature;

      // Dispatch the event
      rawDispatch(event);
    },
    [stateMachine.state]
  );
  const previousStateRef = useRef<OnboardingState>(stateMachine.state);

  // Detect if authtoken is present in URL (for token-based login)
  const hasAuthToken = useMemo(() => {
    const authtoken = new URLSearchParams(window.location.search).get('authtoken');
    return authtoken !== null && authtoken !== 'without';
  }, []);

  /**
   * Determine when it's safe to initialize the state machine
   *
   * Three scenarios:
   * 1. Anonymous user (no user, no authtoken) → initialize immediately
   * 2. Authenticated user → wait for NavigationContext to load persisted state
   * 3. Token-based login → wait for login, then wait for navigation load
   */
  const isReadyToInitialize = useMemo(() => {
    // CRITICAL: Wait for user query to complete before making any decisions
    // This prevents race condition where we initialize before user loads
    if (userLoading) {
      return false;
    }

    // User query completed - now we can trust the user value

    // Case 1: Query completed, no user, no authtoken → truly anonymous user
    // Safe to initialize immediately (no persisted state to restore)
    if (!user && !hasAuthToken) {
      return true;
    }

    // Case 2 & 3: User exists (either already logged in or just logged in via token)
    // Must wait for NavigationContext to load persisted state from database
    if (user) {
      return hasLoaded;
    }

    // Fallback: waiting for something else to resolve
    return false;
  }, [user, userLoading, hasLoaded, hasAuthToken]);

  // Restore state from navigation when it becomes available (before initialization)
  // This should only run ONCE when hasLoaded transitions to true
  useEffect(() => {
    if (!hasRestoredState && hasLoaded && navigation.stateMachine) {
      const restoredState: OnboardingStateMachineState = {
        state: navigation.stateMachine.state as OnboardingState,
        context: navigation.stateMachine.context,
      };

      // Only restore if the persisted state is different from current state
      // This prevents unnecessary restoration when we just persisted the current state
      if (
        restoredState.state !== stateMachine.state ||
        JSON.stringify(restoredState.context) !== JSON.stringify(stateMachine.context)
      ) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[OnboardingStateMachine] Restoring state from navigation:', restoredState);
        }
        dispatch({ type: 'RESTORE', state: restoredState });
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('[OnboardingStateMachine] Skipping restoration - state already matches:', restoredState);
        }
      }

      setHasRestoredState(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLoaded, hasRestoredState]);
  // NOTE: Intentionally NOT depending on navigation.stateMachine to avoid restore loops
  // We only want to restore once when hasLoaded becomes true, not on every persistence update

  // Initialize state machine when ready
  useEffect(() => {
    if (isReadyToInitialize && !isInitialized) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[OnboardingStateMachine] Ready to initialize', {
          scenario:
            !user && !hasAuthToken ? 'anonymous user' : hasAuthToken ? 'token-based login' : 'authenticated user',
          user: user ? `${user.email || 'anonymous'} (id: ${user.id})` : 'none',
          hasAuthToken,
          hasLoaded,
          userLoading,
          wasRestored: hasRestoredState,
          currentState: stateMachine.state,
        });
      }
      setIsInitialized(true);
    }
  }, [
    isReadyToInitialize,
    isInitialized,
    user,
    hasAuthToken,
    hasLoaded,
    userLoading,
    hasRestoredState,
    stateMachine.state,
  ]);

  /**
   * Helper function to check if a transition is valid from the current state
   */
  const canTransition = (eventType: OnboardingEvent['type']): boolean => {
    if (eventType === 'RESET') return true;
    const validTransitions = transitions[stateMachine.state];
    return validTransitions?.[eventType] !== undefined;
  };

  // Side effect: Handle state transitions
  useEffect(() => {
    // Don't run side effects until state machine is properly initialized
    if (!isInitialized) return;

    // Don't run if topics/questionnaires are not loaded yet
    if (topics.length === 0) return;

    const currentState = stateMachine.state;
    const previousState = previousStateRef.current;

    // Skip if state hasn't changed
    if (currentState === previousState) return;

    if (process.env.NODE_ENV === 'development') {
      console.log('[OnboardingStateMachine] Side effect: State changed', {
        from: previousState,
        to: currentState,
        context: stateMachine.context,
      });
    }

    // Update ref for next comparison
    previousStateRef.current = currentState;

    // Handle side effects based on state transitions
    switch (currentState) {
      case OnboardingState.OnboardingChatActive:
        if (process.env.NODE_ENV === 'development') {
          console.log('[OnboardingStateMachine] Side effect: Setting navigation scope to onboarding');
        }
        // Set navigation scope to onboarding
        setNavigation((nav) => {
          nav.scope = 'onboarding';
        });

        // Navigate to /onboarding if user is on a different path
        if (window.location.pathname !== '/onboarding') {
          if (process.env.NODE_ENV === 'development') {
            console.log(
              '[OnboardingStateMachine] Side effect: Navigating to /onboarding from',
              window.location.pathname
            );
          }
          history.push('/onboarding');
        }
        break;

      case OnboardingState.FillingQuestionnaire:
        if (process.env.NODE_ENV === 'development') {
          console.log('[OnboardingStateMachine] Side effect: Creating/opening questionnaire', {
            appId: stateMachine.context.appId,
          });
        }

        if (stateMachine.context.appId && !isCreatingQuestionnaireRef.current) {
          isCreatingQuestionnaireRef.current = true;

          setNavigation((nav) => {
            nav.onboarding = {
              inProgress: true,
              appId: stateMachine.context.appId as OnboardingApps,
            };
          });

          const existingQuestionnaire = questionnaires?.find((q) => q.application.id === stateMachine.context.appId);

          // Special handling for AVV app
          if (stateMachine.context.appId === OnboardingApps.AVV) {
            if (existingQuestionnaire) {
              if (process.env.NODE_ENV === 'development') {
                console.log('[OnboardingStateMachine] Opening existing AVV questionnaire');
              }
              openQuestionnaire({ variables: { localQuestionnaireId: existingQuestionnaire.id } })
                .then((result) => {
                  const questionnaireId = result.data?.openQuestionnaire.questionnaireId;
                  if (questionnaireId) {
                    navigateToPath({ questionnaireId });
                  }
                  isCreatingQuestionnaireRef.current = false;
                })
                .catch((e) => {
                  if (process.env.NODE_ENV === 'development') {
                    console.error('[OnboardingStateMachine] Error opening AVV questionnaire:', e);
                  }
                  isCreatingQuestionnaireRef.current = false;
                });
            } else {
              if (process.env.NODE_ENV === 'development') {
                console.log('[OnboardingStateMachine] Creating new AVV questionnaire with user value');
              }
              addUserValue({ variables: { key: 'DATA_PROCESSOR_TYPE', value: 'AVV', createIndex: true } })
                .then((res) => {
                  const userValueIndex = res.data?.addUserValue.index;
                  return createQuestionnaire({
                    variables: { appId: stateMachine.context.appId!, userValueIndex },
                  });
                })
                .then((result) => {
                  const questionnaireId = result.data?.createQuestionnaire.questionnaireId;
                  if (questionnaireId) {
                    navigateToPath({ questionnaireId });
                  }
                  isCreatingQuestionnaireRef.current = false;
                })
                .catch((e) => {
                  if (process.env.NODE_ENV === 'development') {
                    console.error('[OnboardingStateMachine] Error creating AVV questionnaire:', e);
                  }
                  isCreatingQuestionnaireRef.current = false;
                });
            }
          }
          // Special handling for INFO app
          else if (stateMachine.context.appId === OnboardingApps.INFO) {
            if (existingQuestionnaire) {
              if (process.env.NODE_ENV === 'development') {
                console.log('[OnboardingStateMachine] Opening existing INFO questionnaire');
              }
              openQuestionnaire({ variables: { localQuestionnaireId: existingQuestionnaire.id } })
                .then((result) => {
                  const questionnaireId = result.data?.openQuestionnaire.questionnaireId;
                  if (questionnaireId) {
                    navigateToPath({ questionnaireId });
                  }
                  isCreatingQuestionnaireRef.current = false;
                })
                .catch((e) => {
                  if (process.env.NODE_ENV === 'development') {
                    console.error('[OnboardingStateMachine] Error opening INFO questionnaire:', e);
                  }
                  isCreatingQuestionnaireRef.current = false;
                });
            } else {
              if (process.env.NODE_ENV === 'development') {
                console.log('[OnboardingStateMachine] Creating new INFO questionnaire with user value');
              }
              addUserValue({
                variables: { key: 'INFOFORM_DESCRIPTION', value: 'allg. Datenschutzhinweise', createIndex: true },
              })
                .then((res) => {
                  const userValueIndex = res.data?.addUserValue.index;
                  return createQuestionnaire({
                    variables: { appId: stateMachine.context.appId!, userValueIndex },
                  });
                })
                .then((result) => {
                  const questionnaireId = result.data?.createQuestionnaire.questionnaireId;
                  if (questionnaireId) {
                    navigateToPath({ questionnaireId });
                  }
                  isCreatingQuestionnaireRef.current = false;
                })
                .catch((e) => {
                  if (process.env.NODE_ENV === 'development') {
                    console.error('[OnboardingStateMachine] Error creating INFO questionnaire:', e);
                  }
                  isCreatingQuestionnaireRef.current = false;
                });
            }
          }
          // Default handling for other apps
          else {
            if (existingQuestionnaire) {
              if (process.env.NODE_ENV === 'development') {
                console.log('[OnboardingStateMachine] Opening existing questionnaire');
              }
              openQuestionnaire({ variables: { localQuestionnaireId: existingQuestionnaire.id } })
                .then((result) => {
                  const questionnaireId = result.data?.openQuestionnaire.questionnaireId;
                  if (questionnaireId) {
                    navigateToPath({ questionnaireId });
                  }
                  isCreatingQuestionnaireRef.current = false;
                })
                .catch((e) => {
                  if (process.env.NODE_ENV === 'development') {
                    console.error('[OnboardingStateMachine] Error opening questionnaire:', e);
                  }
                  isCreatingQuestionnaireRef.current = false;
                });
            } else {
              if (process.env.NODE_ENV === 'development') {
                console.log('[OnboardingStateMachine] Creating new questionnaire');
              }
              createQuestionnaire({ variables: { appId: stateMachine.context.appId } })
                .then((result) => {
                  const questionnaireId = result.data?.createQuestionnaire.questionnaireId;
                  if (questionnaireId) {
                    navigateToPath({ questionnaireId });
                  }
                  isCreatingQuestionnaireRef.current = false;
                })
                .catch((e) => {
                  if (process.env.NODE_ENV === 'development') {
                    console.error('[OnboardingStateMachine] Error creating questionnaire:', e);
                  }
                  isCreatingQuestionnaireRef.current = false;
                });
            }
          }
        }
        break;

      case OnboardingState.AwaitingEmailConfirmation:
      case OnboardingState.AwaitingEmail:
      case OnboardingState.DocumentPreviewWithCTA:
        if (process.env.NODE_ENV === 'development') {
          console.log('[OnboardingStateMachine] Side effect: Opening document preview modal');
        }

        if (stateMachine.context.appId === OnboardingApps.WEBSITE_SCAN) {
          // Website-scan flow: set onboarding context and navigate to dashboard (email modal will show there)
          setNavigation((nav) => {
            nav.onboarding = {
              inProgress: true,
              appId: OnboardingApps.WEBSITE_SCAN,
            };
          });
          if (!window.location.pathname.includes('/confirm')) {
            history.push('/dashboard');
          }
        } else if (stateMachine.context.appId) {
          const onboardingTopic = topics.find((t) => t.applications.find((a) => a.id === stateMachine.context.appId));
          const questionnaire = questionnaires?.find((q) => q.application.id === stateMachine.context.appId);

          // do not navigate if route contains /confirm
          if (onboardingTopic && questionnaire && !window.location.pathname.includes('/confirm')) {
            if (process.env.NODE_ENV === 'development') {
              console.log('[OnboardingStateMachine] Navigating to topic and opening document paper');
            }

            // handle AVV and INFO edge cases where we actually need to navigate to the subsections as well
            if (stateMachine.context.appId === OnboardingApps.AVV) {
              navigateDashboard(NavigationSection.Topic, onboardingTopic.id, NavigationSubsection.ContractPartners);
            } else if (stateMachine.context.appId === OnboardingApps.INFO) {
              navigateDashboard(NavigationSection.Topic, onboardingTopic.id, NavigationSubsection.InfoForms);
            } else {
              navigateDashboard(NavigationSection.Topic, onboardingTopic.id || Topics.CHECKUP);
            }

            // Only open document paper for documents and analyses and contracts
            if (
              [ApplicationClass.Document, ApplicationClass.Analysis, ApplicationClass.Contract].includes(
                questionnaire.application.class
              )
            ) {
              setNavigation((nav) => {
                nav.documentPaper = {
                  modalOpen: true,
                  questionnaire: questionnaire,
                  index: 0,
                };
              });
            }
          }
        } else {
          // case for direct account creation without app selection
          if (process.env.NODE_ENV === 'development') {
            console.log('[OnboardingStateMachine] No app selected, navigating to /dashboard');
          }
          history.push('/dashboard');
        }
        break;

      case OnboardingState.AwaitingPassword:
        if (process.env.NODE_ENV === 'development') {
          console.log('[OnboardingStateMachine] Side effect: Opening password modal');
        }

        setNavigation((nav) => {
          nav.documentPaper = {
            ...nav.documentPaper,
            modalOpen: false,
          };
          nav.passwordModalOpen = true;
        });
        break;

      case OnboardingState.Complete:
        if (process.env.NODE_ENV === 'development') {
          console.log('[OnboardingStateMachine] Side effect: Onboarding complete, clearing state');
        }
        // Mark onboarding as no longer in progress, but preserve appId
        // since downstream components (TicketTeaser, Roadblock, etc.) depend on it
        // to route the user to the correct checkout path.
        setNavigation((nav) => {
          nav.onboarding.inProgress = false;
        });

        // Website-scan flow: redirect to technology-scan report
        if (stateMachine.context.appId === OnboardingApps.WEBSITE_SCAN) {
          history.push('/dashboard/topic/dse/technology-scan');
        }
        break;
    }
  }, [
    stateMachine.state,
    stateMachine.context,
    history,
    setNavigation,
    topics,
    questionnaires,
    navigateDashboard,
    navigateToPath,
    createQuestionnaire,
    openQuestionnaire,
    addUserValue,
    isInitialized,
  ]);

  // Side effect: Detect questionnaire completion
  useEffect(() => {
    // Don't run until state machine is properly initialized
    if (!isInitialized) return;

    if (stateMachine.state === OnboardingState.FillingQuestionnaire && navigation.justFinishedQuestionnaireRemoteId) {
      const questionnaire = questionnaires?.find(
        (q) => q.questionnaireId === navigation.justFinishedQuestionnaireRemoteId
      );

      if (questionnaire && questionnaire.questionnaireId) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[OnboardingStateMachine] Questionnaire completed, transitioning to AwaitingEmail', {
            questionnaireId: questionnaire.questionnaireId,
          });
        }

        dispatch({
          type: 'QUESTIONNAIRE_COMPLETED',
          questionnaireId: questionnaire.questionnaireId,
        });
      }
    }
  }, [navigation.justFinishedQuestionnaireRemoteId, stateMachine.state, questionnaires, isInitialized]);

  // Side effect: Sync user state with state machine
  useEffect(() => {
    // Don't run until state machine is properly initialized
    if (!isInitialized) return;
    if (!user) return;

    if (process.env.NODE_ENV === 'development') {
      console.log('[OnboardingStateMachine] Sync effect: Checking user state', {
        currentState: stateMachine.state,
        user: {
          isAnonymous: user.isAnonymous,
          email: user.email,
          confirmed: user.confirmed,
          hasPassword: user.hasPassword,
          conditionsConsent: user.conditionsConsent,
          mailSent: user.mailSent,
        },
      });
    }

    // ========================================================================
    // FORCED STATE CORRECTIONS (Data Integrity Enforcement)
    // These run BEFORE forward progression to ensure state matches user data
    // ========================================================================

    // Force correction: If user has no email but is in advanced states, reset to AwaitingEmail
    if (
      !user.email &&
      (stateMachine.state === OnboardingState.Complete ||
        stateMachine.state === OnboardingState.AwaitingPassword ||
        stateMachine.state === OnboardingState.DocumentPreviewWithCTA ||
        stateMachine.state === OnboardingState.AwaitingEmailConfirmation)
    ) {
      if (process.env.NODE_ENV === 'development') {
        console.log(
          '[OnboardingStateMachine] FORCE CORRECTION: No email in advanced state, restoring to AwaitingEmail'
        );
      }
      dispatch({
        type: 'RESTORE',
        state: {
          state: OnboardingState.AwaitingEmail,
          context: { ...stateMachine.context, email: undefined },
        },
      });
      return;
    }

    // Force correction: If user has no password but is in Complete state, reset to AwaitingPassword
    if (
      !user.hasPassword &&
      (stateMachine.state === OnboardingState.Complete ||
        (user.email && user.confirmed && stateMachine.state === OnboardingState.OnboardingStart))
    ) {
      if (process.env.NODE_ENV === 'development') {
        console.log(
          '[OnboardingStateMachine] FORCE CORRECTION: No password in Complete state, restoring to AwaitingPassword'
        );
      }
      dispatch({
        type: 'RESTORE',
        state: {
          state: OnboardingState.AwaitingPassword,
          context: stateMachine.context,
        },
      });
      return;
    }

    // ========================================================================
    // FORWARD PROGRESSION (Normal State Transitions)
    // ========================================================================

    // If user is created and we're in OnboardingStart, transition to OnboardingChatActive
    if (stateMachine.state === OnboardingState.OnboardingStart && user.isAnonymous) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[OnboardingStateMachine] Sync effect: Anonymous user detected, notifying user creation');
      }
      dispatch({ type: 'ANONYMOUS_USER_CREATED' });
    }

    // If user submits email while in AwaitingEmail, transition
    if (stateMachine.state === OnboardingState.AwaitingEmail && user.email) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[OnboardingStateMachine] Sync effect: Email submitted, transitioning');
      }
      dispatch({ type: 'EMAIL_SUBMITTED', email: user.email });
    }

    // If user confirms email while in AwaitingEmailConfirmation, transition
    if (stateMachine.state === OnboardingState.AwaitingEmailConfirmation && user.confirmed) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[OnboardingStateMachine] Sync effect: Email confirmed, transitioning');
      }
      dispatch({ type: 'EMAIL_CONFIRMED' });
    }

    // Reverse: If user loses confirmation while in DocumentPreviewWithCTA, go back
    if (stateMachine.state === OnboardingState.DocumentPreviewWithCTA && !user.confirmed) {
      if (process.env.NODE_ENV === 'development') {
        console.log(
          '[OnboardingStateMachine] Sync effect: Confirmation reset, transitioning back to AwaitingEmailConfirmation'
        );
      }
      dispatch({ type: 'CONFIRMATION_RESET' });
    }

    // If user has password and is in AwaitingPassword, transition to Complete
    if (stateMachine.state === OnboardingState.AwaitingPassword && user.hasPassword) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[OnboardingStateMachine] Sync effect: Password set, completing onboarding');
      }
      dispatch({ type: 'PASSWORD_SUBMITTED' });
      return; // Early return to prevent fast-forward logic from running
    }

    // If user is already fully onboarded but not in Complete state, fast-forward to Complete
    // This handles cases where a fully onboarded user logs in or state is corrupted
    if (stateMachine.state !== OnboardingState.Complete && user.email && user.confirmed && user.hasPassword) {
      if (process.env.NODE_ENV === 'development') {
        console.log(
          '[OnboardingStateMachine] Sync effect: User already fully onboarded, resetting to initial then will fast-forward'
        );
      }
      // Reset to start, then the other checks will take them through the flow
      // Or better: directly restore to Complete state
      dispatch({
        type: 'RESTORE',
        state: {
          state: OnboardingState.Complete,
          context: stateMachine.context,
        },
      });
    }
  }, [user, stateMachine.state, isInitialized]);

  // Side effect: Persist state machine to navigation context
  useEffect(() => {
    // Don't persist until state machine is properly initialized
    if (!isInitialized || !user) return;

    if (process.env.NODE_ENV === 'development') {
      console.log('[OnboardingStateMachine] Persisting state to navigation context', {
        state: stateMachine.state,
        context: stateMachine.context,
      });
    }

    setNavigation((nav) => {
      nav.stateMachine = {
        state: stateMachine.state,
        context: stateMachine.context,
      };
    });
  }, [stateMachine.state, stateMachine.context, setNavigation, isInitialized, user]);

  const value: OnboardingStateMachineContextValue = {
    currentState: stateMachine.state,
    context: stateMachine.context,
    dispatch,
    canTransition,
  };

  return <OnboardingStateMachineContext.Provider value={value}>{children}</OnboardingStateMachineContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access the onboarding state machine
 */
export function useOnboardingStateMachine() {
  const context = useContext(OnboardingStateMachineContext);

  if (context === undefined) {
    throw new Error('useOnboardingStateMachine must be used within OnboardingStateMachineProvider');
  }

  return context;
}

/**
 * Hook with helper functions to trigger specific transitions
 */
export function useOnboardingActions() {
  const { dispatch, canTransition } = useOnboardingStateMachine();

  return {
    /**
     * Notify that an anonymous user has been created
     * Transitions: OnboardingStart → OnboardingChatActive
     */
    notifyAnonymousUserCreated: () => {
      dispatch({ type: 'ANONYMOUS_USER_CREATED' });
    },

    /**
     * Select an onboarding application
     * Transitions: OnboardingChatActive → FillingQuestionnaire
     */
    selectApp: (appId: string) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[OnboardingStateMachine] Action: selectApp called', { appId });
      }
      dispatch({ type: 'APP_SELECTED', appId });
    },

    /**
     * Deselect the current application and return to chat
     * Transitions: FillingQuestionnaire → OnboardingChatActive
     */
    deselectApp: () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[OnboardingStateMachine] Action: deselectApp called');
      }
      dispatch({ type: 'APP_DESELECTED' });
    },

    /**
     * Mark questionnaire as completed
     * Transitions: FillingQuestionnaire → AwaitingEmail
     */
    completeQuestionnaire: (questionnaireId: string) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[OnboardingStateMachine] Action: completeQuestionnaire called', { questionnaireId });
      }
      dispatch({ type: 'QUESTIONNAIRE_COMPLETED', questionnaireId });
    },

    /**
     * Request account creation
     * Transitions: OnboardingChatActive → AwaitingEmail
     */
    accountCreateRequested: (appId?: string) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[OnboardingStateMachine] Action: accountCreateRequested called');
      }
      dispatch({ type: 'ACCOUNT_CREATE_REQUESTED', appId });
    },

    /**
     * Submit email address
     * Transitions: AwaitingEmail → AwaitingEmailConfirmation
     */
    submitEmail: (email: string) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[OnboardingStateMachine] Action: submitEmail called', { email });
      }
      dispatch({ type: 'EMAIL_SUBMITTED', email });
    },

    /**
     * Confirm email has been verified
     * Transitions: AwaitingEmailConfirmation → DocumentPreviewWithCTA
     */
    confirmEmail: () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[OnboardingStateMachine] Action: confirmEmail called');
      }
      dispatch({ type: 'EMAIL_CONFIRMED' });
    },

    /**
     * Click the call-to-action button in document preview
     * Transitions: DocumentPreviewWithCTA → AwaitingPassword
     */
    clickCTA: () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[OnboardingStateMachine] Action: clickCTA called');
      }
      dispatch({ type: 'CTA_CLICKED' });
    },

    /**
     * Submit password to complete onboarding
     * Transitions: AwaitingPassword → Complete
     */
    submitPassword: (password: string) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[OnboardingStateMachine] Action: submitPassword called');
      }
      dispatch({ type: 'PASSWORD_SUBMITTED' });
    },

    /**
     * Reset the onboarding state machine to initial state
     */
    reset: () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[OnboardingStateMachine] Action: reset called');
      }
      dispatch({ type: 'RESET' });
    },

    /**
     * Check if a specific transition is valid from current state
     */
    canTransition,
  };
}

// ============================================================================
// Utility Helpers
// ============================================================================

/**
 * Type guard to check if currently in a specific state
 */
export function isInState(currentState: OnboardingState, targetState: OnboardingState): boolean {
  return currentState === targetState;
}

/**
 * Check if onboarding is complete
 */
export function isOnboardingComplete(currentState: OnboardingState): boolean {
  return currentState === OnboardingState.Complete;
}

/**
 * Check if onboarding is in progress
 */
export function isOnboardingInProgress(currentState: OnboardingState): boolean {
  return currentState !== OnboardingState.Complete && currentState !== OnboardingState.OnboardingStart;
}
